import {
  Chip,
  Card,
  CardActions,
  CardMedia,
  Box,
  CardHeader,
  Typography,
  Theme,
  useMediaQuery,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { buildCardTestId, formatCurrency } from "@covertree/utils";
import { StyledRadio, Tooltip } from "@covertree/ui";
import { ComponentProps, useEffect, useId, useRef, useState } from "react";
import { Offer } from "@covertree/new-business-graphql";
import {
  getPackageCoveragesMap,
  PackageCoverageAccessor,
  ScreenWithCoverages,
} from "@covertree/new-business-mappings";
import {
  selectPolicy,
  useNewBusinessSelector,
} from "@covertree/new-business-redux";

import { PersonalizedQuotesListCardDetails } from "./PersonalizedQuotesListCardDetails";
import { PersonalizedPlanDetailsType } from "../forms/personalizedPlans";

const TOOLTIP_OPENED_EVENT = "ct-tooltip-opened";

function TooltipWithGlobalClose(props: ComponentProps<typeof Tooltip>) {
  const id = useId();
  const [remountKey, setRemountKey] = useState(0);
  const isActiveRef = useRef(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close this tooltip when another one opens
  useEffect(() => {
    const handler = (e: Event) => {
      const { tooltipId } = (e as CustomEvent<{ tooltipId: string }>).detail;
      if (tooltipId !== id) {
        isActiveRef.current = false;
        setRemountKey((k) => k + 1);
      }
    };
    window.addEventListener(TOOLTIP_OPENED_EVENT, handler);
    return () => window.removeEventListener(TOOLTIP_OPENED_EVENT, handler);
  }, []);

  // Reset active state when clicking outside this wrapper (e.g. ClickAway closes tooltip)
  useEffect(() => {
    const resetOnOutsideClick = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) {
        isActiveRef.current = false;
      }
    };
    window.addEventListener("click", resetOnOutsideClick, true);
    return () => window.removeEventListener("click", resetOnOutsideClick, true);
  }, []);

  // Close on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (isActiveRef.current) {
        isActiveRef.current = false;
        setRemountKey((k) => k + 1);
      }
    };
    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, []);

  // Bubble phase: fires on every click; broadcasts that this tooltip is now open
  const handleClick = () => {
    isActiveRef.current = true;
    window.dispatchEvent(
      new CustomEvent(TOOLTIP_OPENED_EVENT, {
        detail: { tooltipId: id },
      })
    );
  };

  const closeTooltip = () => {
    isActiveRef.current = false;
    setRemountKey((k) => k + 1);
  };

  return (
    <div ref={wrapperRef} onClick={handleClick}>
      <Tooltip
        key={remountKey}
        {...props}
        componentsProps={{
          ...props.componentsProps,
          tooltip: {
            ...props.componentsProps?.tooltip,
            onClick: closeTooltip,
          },
        }}
      />
    </div>
  );
}

interface Props {
  personalizedPlan: PersonalizedPlanDetailsType;
  shouldDisplayPremisesLiability: boolean;
  isTenantFlow: boolean;
  selected?: boolean;
  onPlanPick: (offer: Offer) => void;
}

export function PersonalizedQuotesListCard({
  personalizedPlan,
  shouldDisplayPremisesLiability,
  isTenantFlow,
  selected,
  onPlanPick,
}: Props) {
  const {
    name,
    offer,
    quote,
    title,
    monthlyPrice,
    icon,
    chosenPercentage,
    isRecommended,
  } = personalizedPlan;
  const policy = useNewBusinessSelector(selectPolicy);
  const isRoofPaymentCustomizable =
    !!policy?.units[0]?.unitLevelCoverages.roofPaymentSchedule
      .valueIsCustomizable;
  const testId = buildCardTestId(`offer-${name}-test-card`);

  const theme = useTheme();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );

  return (
    <Card
      role="option"
      aria-selected={selected}
      data-testid={`${testId}`}
      aria-labelledby={`${testId}-label`}
      sx={{
        display: "flex",
        cursor: "pointer",
        flexWrap: "wrap",
        alignItems: "center",
        borderWidth: 1,
        "&:hover": {
          boxShadow: theme.shadows[2],
        },
        padding: theme.spacing(2.875),
        border: selected
          ? `1px solid ${theme.palette.primary.main}`
          : undefined,
      }}
      onClick={() => onPlanPick(offer)}>
      {isRecommended && (
        <Chip
          color="secondary"
          label="Recommended"
          size="small"
          sx={{
            width: "100%",
            marginBottom: theme.spacing(2.75),
            [theme.breakpoints.up("sm")]: {
              display: "none",
            },
          }}
        />
      )}
      <CardActions
        sx={{
          padding: 0,
          height: theme.spacing(8),
          [theme.breakpoints.up(375)]: {
            marginRight: theme.spacing(1),
          },
          [theme.breakpoints.up("sm")]: {
            marginRight: theme.spacing(3),
          },
        }}>
        <StyledRadio
          data-testid={`${testId}-radio-button`}
          checked={selected}
        />
      </CardActions>
      <CardMedia
        image={icon}
        data-testid={`${testId}-image`}
        sx={{
          width: 64,
          height: 64,
          marginX: theme.spacing(2),
        }}
      />
      <Box
        display="flex"
        flexGrow={1}
        justifyContent="space-between"
        sx={{ marginLeft: { xs: 1, sm: 0 } }}>
        <Box position="relative">
          <CardHeader
            title={
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                sx={{
                  gap: 1,
                }}>
                <span>{title}</span>
                {isRecommended && (
                  <Chip
                    color="secondary"
                    label="Recommended"
                    size="small"
                    sx={{
                      display: "none",
                      top: 0,
                      [theme.breakpoints.up("sm")]: {
                        display: "flex",
                        position: "absolute",
                        right: 0,
                        transform: "translateX(70%)",
                      },
                      [theme.breakpoints.up("md")]: {
                        transform: "translateX(75%)",
                      },
                      [theme.breakpoints.up(1050)]: {
                        transform: "translateX(60%)",
                      },
                    }}
                  />
                )}
              </Box>
            }
            titleTypographyProps={{
              "data-testid": `${testId}-title`,
              id: `${testId}-label`,
            }}
            subheaderTypographyProps={{
              "data-testid": `${testId}-subtitle`,
              sx: {
                fontSize: {
                  xs: "1rem",
                  md: "1.25rem",
                },
              },
            }}
            subheader={
              chosenPercentage ? `${chosenPercentage}% choose this` : undefined
            }
            sx={{
              flexShrink: 1,
              flexDirection: { xs: "column", md: "row" },
              alignItems: "start",
            }}
          />

          <Box display={isMobile ? "flex" : "none"} flexDirection="column">
            <Box display="flex" alignItems="center">
              <Typography
                variant="h3"
                sx={{
                  [theme.breakpoints.down(1250)]: {
                    fontSize: "1.8rem",
                  },

                  [theme.breakpoints.down(1100)]: {
                    fontSize: "1.5rem",
                  },
                  [theme.breakpoints.down("sm")]: {
                    fontSize: "1.75rem",
                  },
                }}>
                {formatCurrency(monthlyPrice, 2)}
              </Typography>
              <Typography
                sx={{
                  marginTop: theme.spacing(2),
                  [theme.breakpoints.down(1250)]: {
                    fontSize: "1rem",
                  },

                  [theme.breakpoints.down(1100)]: {
                    fontSize: "0.8rem",
                    marginTop: theme.spacing(1.5),
                  },
                  [theme.breakpoints.down("sm")]: {
                    fontSize: "1.25rem",
                    marginTop: theme.spacing(2),
                  },
                }}>
                /mo
              </Typography>
            </Box>
            <Typography variant="subtitle1">
              ({formatCurrency(offer.pricing.totalDue, 2)}/year)
            </Typography>
          </Box>
        </Box>
        <Box flexDirection="column" display={isMobile ? "none" : "flex"}>
          <Box display="flex" alignItems="center">
            <Typography
              variant="h3"
              sx={{
                [theme.breakpoints.down("lg")]: {
                  fontSize: "1.8rem",
                },

                [theme.breakpoints.down(1100)]: {
                  fontSize: "1.5rem",
                },
                [theme.breakpoints.down("sm")]: {
                  fontSize: "2rem",
                },
              }}>
              {formatCurrency(monthlyPrice, 2)}
            </Typography>
            <Typography
              sx={{
                marginTop: theme.spacing(2),
                [theme.breakpoints.down("lg")]: {
                  fontSize: "1rem",
                },

                [theme.breakpoints.down(1100)]: {
                  fontSize: "0.8rem",
                  marginTop: theme.spacing(1.5),
                },
                [theme.breakpoints.down("sm")]: {
                  fontSize: "1.25rem",
                  marginTop: theme.spacing(2),
                },
              }}>
              /mo
            </Typography>
          </Box>
          <Typography variant="subtitle1" ml={{ xs: 0, lg: 3 }}>
            ({formatCurrency(offer.pricing.totalDue, 2)}/year)
          </Typography>
        </Box>
      </Box>

      <Box
        display="flex"
        flexDirection="column"
        flexGrow={1}
        justifyContent="space-between"
        sx={{
          marginLeft: {
            xs: 0,
            sm: 16,
          },
          width: "100%",
        }}>
        {isMobile && (
          <Divider
            sx={{
              marginTop: 2,
            }}
          />
        )}
        <PersonalizedQuotesListCardDetails
          quote={quote}
          isTenantFlow={isTenantFlow}
          hasPremisesLiability={shouldDisplayPremisesLiability}
          homeCoverageDescription={offer.homeCoverageDescription}
          yourBelongingsDescription={offer.yourBelongingsDescription}
        />
      </Box>

      {offer.quote.roofPaymentSchedule && (
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          sx={{
            marginTop: 2,
            marginLeft: {
              xs: 0,
              sm: 16,
            },
            width: "100%",
          }}>
          <Box onClick={(e) => e.stopPropagation()}>
            <TooltipWithGlobalClose
              dataTestId="roof-payment-schedule"
              tooltip={getPackageCoveragesMap(ScreenWithCoverages.PlanDetails)[
                PackageCoverageAccessor.RoofPaymentSchedule
              ].getDescription({ isRoofPaymentCustomizable })}
            />
          </Box>
          <Typography
            variant="body2"
            sx={{
              marginLeft: {
                xs: 2,
              },
            }}>
            Roof Payment Schedule: Included
          </Typography>
        </Box>
      )}
    </Card>
  );
}
